import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { LoadingBarModule } from '@ngx-loading-bar/core';

import { isNullOrUndefined } from '@app/util/util';
import {
  HeaderReservationInfoWidget, HeaderSearchDataInfoWidget, HeaderType, HeaderWidget, HeaderWidgetComponentType,
  HeaderWidgetConfig, HeaderSupportType, HeaderWidgetType, HeaderWidgetsAvailable, LabelAlignType
} from '@design-system/components/templates/header/entities/header.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';
import { InputSearchSize } from '@design-system/components/molecules/input-search/entities/input-search.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';
import { DividerOrientation } from '@design-system/components/atoms/divider/entities/divider.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { ButtonIconDsComponent } from '@design-system/components/molecules/button-icon/button-icon.component';
import { ButtonFloatingDsComponent } from '@design-system/components/organisms/button-floating/button-floating.component';
import { InputSearchDsComponent } from '@design-system/components/molecules/input-search/input-search.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';
import { DividerDsComponent } from '@design-system/components/atoms/divider/divider.component';

@Component({
  selector: 'app-ds-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    LoadingBarModule,
    ButtonFloatingDsComponent,
    InputSearchDsComponent,
    ImageDsComponent,
    ButtonIconDsComponent,
    IconDsComponent,
    TypographyDsComponent,
    DividerDsComponent,
  ]
})
export class HeaderDsComponent {

  @Input() type = HeaderType.Generic;
  @Input() widgetConfig: HeaderWidgetConfig[] = [];
  @Input() logo: string;
  @Input() isTransparent = false;

  @Output() clickAction: EventEmitter<HeaderWidget> = new EventEmitter<HeaderWidget>();

  public readonly logoSize = ImageSize.None;

  public readonly buttonIconSize = ButtonIconSize.LG;

  public readonly fontFamily = TypographyFontFamily;

  public readonly inputSearchSize = InputSearchSize.SM;

  public readonly loaderBarColor = 'var(--line-accent)';

  public readonly dividerOrientation = DividerOrientation.Vertical;

  public readonly iconFillHighlight = IconFillColor.Highlight;

  constructor() { }

  get fontSize(): typeof TypographyFontSize {
    return TypographyFontSize;
  }

  get widgetComponentTypes(): typeof HeaderWidgetComponentType {
    return HeaderWidgetComponentType;
  }

  get widgetsByConfig(): HeaderWidget[] {
    return this.widgetConfig?.map(wc => this.availablesWidgetsByType.find(ew => ew?.Type === wc?.Type)).filter(we => !!we) ?? [];
  }

  get logoIsEnabled(): boolean {
    return this.type === HeaderType.HomeLG || this.type === HeaderType.HomeSM;
  }

  private get isFloating(): boolean {
    return this.type === HeaderType.FloatingRG || this.type === HeaderType.FloatingSM;
  }

  private get availablesWidgetsByType(): HeaderWidget[] {
    return HeaderWidgetsAvailable.filter(widget => {
      return this.widgetConfig?.map(wc => wc?.Type)?.includes(widget.Type) && widget.AvailableIn.includes(this.type);
    });
  }

  public getHeaderWidgetComponentType(widget: HeaderWidget): HeaderWidgetComponentType {
    switch (true) {
      case widget.Type === HeaderWidgetType.SearchInfo:
        return HeaderWidgetComponentType.SearchDataInfo;
      case widget.Type === HeaderWidgetType.ReservationInfo:
        return HeaderWidgetComponentType.ReservationDataInfo;
      case widget.Type === HeaderWidgetType.InputSearch:
        return HeaderWidgetComponentType.InputSearch;
      case widget.Type === HeaderWidgetType.Edit:
      case widget.Type === HeaderWidgetType.Clean:
        return HeaderWidgetComponentType.Label;
      case widget.Type === HeaderWidgetType.Title:
        return HeaderWidgetComponentType.Title;
      case widget.Type === HeaderWidgetType.CardBrand:
      case widget.Type === HeaderWidgetType.CardSelected:
        return HeaderWidgetComponentType.Card;
      case this.type === HeaderType.FloatingRG && widget.Type === HeaderWidgetType.MyTrips:
      case this.type === HeaderType.FloatingRG && widget.Type === HeaderWidgetType.Support:
        return HeaderWidgetComponentType.ButtonFloating;
      case this.isFloating && widget.Type === HeaderWidgetType.Alert:
      case this.type === HeaderType.FloatingSM && widget.Type === HeaderWidgetType.MyTrips:
      case this.type === HeaderType.FloatingSM && widget.Type === HeaderWidgetType.Support:
      case this.type === HeaderType.Reservation && widget.Type === HeaderWidgetType.Support:
      case this.type === HeaderType.Generic && widget.Type === HeaderWidgetType.MyTrips:
        return HeaderWidgetComponentType.ButtonIcon;
      case !this.isFloating && widget.Type === HeaderWidgetType.Alert:
      case !this.isFloating && widget.Type === HeaderWidgetType.MyTrips:
      case !this.isFloating && widget.Type === HeaderWidgetType.Support:
      case !this.isFloating && widget.Type === HeaderWidgetType.Rappi:
        return HeaderWidgetComponentType.Tab;
      default:
        return HeaderWidgetComponentType.ButtonIcon;
    }
  }

  public widgetLabel(widget: HeaderWidget, componentType: HeaderWidgetComponentType): string {
    const label = this.getWidgetConfig(widget)?.Label ?? null;
    if (this.type === HeaderType.Reservation && componentType === HeaderWidgetComponentType.ButtonIcon) {
      return widget.Type === HeaderWidgetType.Support ? label : null;
    } else {
      return componentType !== HeaderWidgetComponentType.ButtonIcon ? label : null;
    }
  }

  public widgetLabelIcon(widget: HeaderWidget): Icon {
    return this.getWidgetConfig(widget)?.LabelIcon ?? null;
  }

  public widgetLabelAlign(widget: HeaderWidget): LabelAlignType {
    return this.getWidgetConfig(widget)?.LabelAlign ?? 'left';
  }

  public widgetSearchDataInfo(widget: HeaderWidget): HeaderSearchDataInfoWidget {
    return this.getWidgetConfig(widget)?.SearchDataInfo ?? null;
  }

  public widgetReservationDataInfo(widget: HeaderWidget): HeaderReservationInfoWidget {
    return this.getWidgetConfig(widget)?.ReservationInfo ?? null;
  }

  public widgetIsActivated(widget: HeaderWidget): boolean {
    return !!this.getWidgetConfig(widget)?.IsActivated;
  }

  public widgetLogo(widget: HeaderWidget): string {
    return this.getWidgetConfig(widget)?.Logo ?? null;
  }

  public widgetLink(widget: HeaderWidget): string {
    return this.getWidgetConfig(widget)?.Link ?? null;
  }

  public widgetLoading(widget: HeaderWidget): boolean {
    return !!this.getWidgetConfig(widget)?.Loading;
  }

  public widgetLabelColor(widget: HeaderWidget): TypographyColor {
    switch (widget.Type) {
      case HeaderWidgetType.CardBrand:
      case HeaderWidgetType.CardSelected:
        return TypographyColor.Invert;
      case HeaderWidgetType.SearchInfo:
      case HeaderWidgetType.ReservationInfo:
      case HeaderWidgetType.Title:
      case HeaderWidgetType.Edit:
      case HeaderWidgetType.Clean:
        return TypographyColor.Primary;
      default:
        return TypographyColor.Secondary;
    }
  }

  public widgetIcon(widget: HeaderWidget): Icon {
    return widget?.Type === HeaderWidgetType.Support &&
      this.getWidgetConfig(widget)?.SupportType === HeaderSupportType.Device ? Icon.Support : widget?.Icon;
  }

  public widgetIconColor(widget: HeaderWidget): IconFillColor {
    switch (widget.Type) {
      case HeaderWidgetType.CardBrand:
      case HeaderWidgetType.CardSelected:
        return IconFillColor.Light;
      case HeaderWidgetType.SearchInfo:
        return IconFillColor.Primary;
      default:
        return IconFillColor.Secondary;
    }
  }

  public widgetIconSize(widget: HeaderWidget, icon: Icon | undefined = undefined): number {
    switch (widget.Type) {
      case HeaderWidgetType.Rappi:
        return 22;
      case HeaderWidgetType.CardSelected:
        return 20;
      case HeaderWidgetType.Support:
        return 17;
      case HeaderWidgetType.Edit:
        return 14;
      default:
        return icon === Icon.Calendar ? 12 : 16;
    }
  }

  public widgetTabLabelIsEnabled(widget: HeaderWidget): boolean {
    return widget.Type === HeaderWidgetType.Rappi ? this.type !== HeaderType.HomeSM : true;
  }

  public widgetTabRightDividerIsEnabled(widget: HeaderWidget): boolean {
    return widget.Type !== HeaderWidgetType.Rappi && widget.Type !== HeaderWidgetType.MyTrips;
  }

  public widgetTabLeftDividerIsEnabled(widget: HeaderWidget): boolean {
    return widget.Type === HeaderWidgetType.Rappi;
  }

  public editEventBySearchData(): void {
    const editWidget = this.widgetsByConfig?.find(widget => widget.Type === HeaderWidgetType.Edit);
    if (!isNullOrUndefined(editWidget)) {
      this.clickAction.emit(editWidget);
    }
  }

  public trackById(index: number): number {
    return index;
  }

  private getWidgetConfig(widget: HeaderWidget): HeaderWidgetConfig {
    return this.widgetConfig?.find(wc => wc?.Type === widget?.Type);
  }

}
